import axios from 'axios'
import { Tenant } from '@/types'

/**
  POC — Native-to-Web SSO ("session transfer"): the bridge from a token-only passkey
  login to a real Auth0 browser session.

  Why this exists. The webauthn grant authenticates the user but creates no session —
  its id_token has no `sid` and no `https://siteminder.com/session` claim, and the
  browser never touches the Auth0 domain so no SSO cookie is set. Everything in authx
  that routes through `/authorize` therefore breaks after a native passkey login:
  `checkSession`, `getUserToken` (the `/me/` token enrolment needs) and
  `authorizeRedirectUri` (the post-login product hand-off).

  Session transfer closes that gap: exchange the refresh token for a short-lived,
  single-use session_transfer_token, present it to `/authorize`, and Auth0 creates the
  web session (setting the cookie) and returns an authorization **code** — which is
  exactly the input the existing `/authenticate` pipeline already consumes. So the
  id_token authx actually reads comes from the ordinary code exchange, `sid` and custom
  claims intact, and the grant's claim-less id_token is discarded as a bootstrap artefact.

  Contract notes, learned the hard way:

  - It is NOT an RFC 8693 token exchange. It is a plain `refresh_token` grant with a
    magic audience, and the session transfer token comes back as `access_token`.
    Confirmed against react-native-auth0 `ssoExchange` (AuthenticationOrchestrator.ts).
    Sending `subject_token_type`/`requested_token_type` fails with
    "Invalid subject_token_type and requested_token_type combination".
  - The refresh token only exists if the webauthn grant was called WITHOUT an
    `audience` — Auth0 drops `offline_access` unless the requested audience's API
    allows offline access, and `Auth0 My Account API` (is_system) does not.
  - The token is single use and lives ~60s: mint and navigate with nothing in between.
  - `session_transfer_token` is not in auth0-js's `/authorize` parameter whitelist, so
    `webAuth.authorize()` would silently drop it — hence the URL is built by hand.
  - Enterprise-plan gated per Auth0's Native-to-Web SSO docs.
* */

export const mintSessionTransferToken = async (tenant: Tenant, refreshToken: string): Promise<string> => {

  const domain = tenant.loginDomain

  const { data } = await axios.post(`https://${domain}/oauth/token`, {
    client_id: tenant.loginClientId,
    grant_type: 'refresh_token',
    audience: `urn:${domain}:session_transfer`,
    refresh_token: refreshToken,
  })

  // eslint-disable-next-line no-console
  console.log('[session-transfer-poc] mint response', {
    issued_token_type: data?.issued_token_type,
    expires_in: data?.expires_in,
    session_transfer_token: data?.access_token ? `present (len ${data.access_token.length})` : 'MISSING',
  })

  // Yes: `access_token` IS the session transfer token.
  return data.access_token

}

export const buildSessionTransferAuthorizeUrl = (
  tenant: Tenant,
  args: { sessionTransferToken: string, redirectUri: string, state?: string },
): string => {

  const url = new URL(`https://${tenant.loginDomain}/authorize`)

  url.searchParams.set('client_id', tenant.loginClientId)
  url.searchParams.set('response_type', 'code')
  url.searchParams.set('redirect_uri', args.redirectUri)
  url.searchParams.set('scope', 'openid profile email')
  url.searchParams.set('state', args.state || `poc-${Date.now()}`)
  url.searchParams.set('session_transfer_token', args.sessionTransferToken)

  return url.toString()

}

/**
  Exchanges the code ourselves so the POC can read the id_token the real pipeline would
  get. Public client, no secret and no PKCE, so client_id + code + redirect_uri is all
  it takes. Production does this in auth-api — this is diagnostics only.
* */
export const exchangeCodeForTokens = async (tenant: Tenant, code: string, redirectUri: string): Promise<any> => {

  const { data } = await axios.post(`https://${tenant.loginDomain}/oauth/token`, {
    grant_type: 'authorization_code',
    client_id: tenant.loginClientId,
    code,
    redirect_uri: redirectUri,
  })

  return data

}
